import type { FieldPack } from '../../../../core/fields/types';
import { FieldType } from '../../../../core/fields/types';

export const generalResearchFieldPack: FieldPack = {
  id: 'genogram-general-research',
  name: 'General Research & Genealogy',
  description: 'Fields for research, data collection, and genealogical studies',
  version: '1.0.0',
  dependencies: ['genogram-base'],
  fields: [
    {
      id: 'researchId',
      label: 'fields.research.researchId.label',
      description: 'fields.research.researchId.description',
      type: FieldType.TEXT,
      tags: ['research', 'metadata'],
      priority: 95,
      placeholder: 'fields.research.researchId.placeholder',
      namespace: 'research',
    },
    {
      id: 'cohort',
      label: 'fields.research.cohort.label',
      description: 'fields.research.cohort.description',
      type: FieldType.TEXT,
      tags: ['research', 'metadata'],
      priority: 90,
      placeholder: 'fields.research.cohort.placeholder',
      namespace: 'research',
    },
    {
      id: 'consentStatus',
      label: 'fields.research.consentStatus.label',
      description: 'fields.research.consentStatus.description',
      type: FieldType.SELECT,
      tags: ['research', 'metadata'],
      priority: 85,
      options: [
        { value: 'consented', label: 'fields.research.consentStatus.options.consented' },
        { value: 'declined', label: 'fields.research.consentStatus.options.declined' },
        { value: 'pending', label: 'fields.research.consentStatus.options.pending' },
        { value: 'withdrawn', label: 'fields.research.consentStatus.options.withdrawn' },
        { value: 'not_applicable', label: 'fields.research.consentStatus.options.not_applicable' },
      ],
      namespace: 'research',
    },
    {
      id: 'dataQuality',
      label: 'fields.research.dataQuality.label',
      description: 'fields.research.dataQuality.description',
      type: FieldType.SELECT,
      tags: ['research', 'metadata'],
      priority: 80,
      options: [
        { value: 'verified', label: 'fields.research.dataQuality.options.verified' },
        { value: 'self_reported', label: 'fields.research.dataQuality.options.self_reported' },
        { value: 'incomplete', label: 'fields.research.dataQuality.options.incomplete' },
        { value: 'conflicting', label: 'fields.research.dataQuality.options.conflicting' },
        { value: 'unknown', label: 'fields.research.dataQuality.options.unknown' },
      ],
      namespace: 'research',
    },
    {
      id: 'dataSources',
      label: 'fields.research.dataSources.label',
      description: 'fields.research.dataSources.description',
      type: FieldType.MULTI_SELECT,
      tags: ['research', 'metadata'],
      priority: 75,
      options: [
        { value: 'interview', label: 'fields.research.dataSources.options.interview' },
        { value: 'medical_records', label: 'fields.research.dataSources.options.medical_records' },
        { value: 'vital_records', label: 'fields.research.dataSources.options.vital_records' },
        { value: 'census', label: 'fields.research.dataSources.options.census' },
        { value: 'family_documents', label: 'fields.research.dataSources.options.family_documents' },
        { value: 'dna', label: 'fields.research.dataSources.options.dna' },
        { value: 'public_records', label: 'fields.research.dataSources.options.public_records' },
      ],
      namespace: 'research',
    },
    {
      id: 'collectionDate',
      label: 'fields.research.collectionDate.label',
      description: 'fields.research.collectionDate.description',
      type: FieldType.DATE,
      tags: ['research', 'metadata', 'timeline'],
      priority: 70,
      namespace: 'research',
    },
    {
      id: 'followUpStatus',
      label: 'fields.research.followUpStatus.label',
      description: 'fields.research.followUpStatus.description',
      type: FieldType.SELECT,
      tags: ['research', 'metadata'],
      priority: 65,
      options: [
        { value: 'active', label: 'fields.research.followUpStatus.options.active' },
        { value: 'completed', label: 'fields.research.followUpStatus.options.completed' },
        { value: 'lost', label: 'fields.research.followUpStatus.options.lost' },
        { value: 'withdrawn', label: 'fields.research.followUpStatus.options.withdrawn' },
        { value: 'deceased', label: 'fields.research.followUpStatus.options.deceased' },
      ],
      namespace: 'research',
    },
    {
      id: 'biospecimen',
      label: 'fields.research.biospecimen.label',
      description: 'fields.research.biospecimen.description',
      type: FieldType.MULTI_SELECT,
      tags: ['research', 'genetic', 'hereditary'],
      priority: 60,
      options: [
        { value: 'dna', label: 'fields.research.biospecimen.options.dna' },
        { value: 'blood', label: 'fields.research.biospecimen.options.blood' },
        { value: 'tissue', label: 'fields.research.biospecimen.options.tissue' },
        { value: 'saliva', label: 'fields.research.biospecimen.options.saliva' },
        { value: 'none', label: 'fields.research.biospecimen.options.none' },
      ],
      namespace: 'research',
    },
    {
      id: 'confidentialityLevel',
      label: 'fields.research.confidentialityLevel.label',
      description: 'fields.research.confidentialityLevel.description',
      type: FieldType.SELECT,
      tags: ['research', 'metadata'],
      priority: 55,
      options: [
        { value: 'public', label: 'fields.research.confidentialityLevel.options.public' },
        { value: 'anonymous', label: 'fields.research.confidentialityLevel.options.anonymous' },
        { value: 'confidential', label: 'fields.research.confidentialityLevel.options.confidential' },
        { value: 'restricted', label: 'fields.research.confidentialityLevel.options.restricted' },
      ],
      namespace: 'research',
    },
    {
      id: 'publicationStatus',
      label: 'fields.research.publicationStatus.label',
      description: 'fields.research.publicationStatus.description',
      type: FieldType.SELECT,
      tags: ['research', 'metadata'],
      priority: 50,
      options: [
        { value: 'not_published', label: 'fields.research.publicationStatus.options.not_published' },
        { value: 'in_review', label: 'fields.research.publicationStatus.options.in_review' },
        { value: 'published', label: 'fields.research.publicationStatus.options.published' },
        { value: 'embargoed', label: 'fields.research.publicationStatus.options.embargoed' },
      ],
      namespace: 'research',
    },
    {
      id: 'researchNotes',
      label: 'fields.research.researchNotes.label',
      description: 'fields.research.researchNotes.description',
      type: FieldType.RICH_TEXT,
      tags: ['notes', 'documentation'],
      priority: 45,
      multiline: true,
      placeholder: 'fields.research.researchNotes.placeholder',
      namespace: 'research',
    },
  ],
};
